import { useAnalytics } from '@/composables/useAnalytics'
import { QueryStrings } from '@/config/references'
import {
  IMappedRemedy,
  isReferenceTab,
  TContentType,
  TReferenceClickOrigin,
  TReferenceTab
} from '@lifk/types'
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export const useReferencesStore = defineStore('references', () => {
  const route = useRoute()
  const router = useRouter()
  const { trackButtonClick } = useAnalytics()

  const queryValues = computed<
    | {
        contentType: TContentType
        contentKey?: string
        tab: string | undefined
      }
    | undefined
  >(() => {
    if (!route.query) return undefined

    const routeContentType = route.query[QueryStrings.contentType]
    const routeKey = route.query[QueryStrings.key]
    if (!routeContentType) return undefined

    let tab: TReferenceTab | undefined = undefined
    const routeTab = route.query[QueryStrings.tab]
      ? route.query[QueryStrings.tab]?.toString()
      : undefined
    if (routeTab && isReferenceTab(routeTab)) {
      tab = routeTab
    }
    return {
      contentType: routeContentType as TContentType,
      contentKey: routeKey?.toString(),
      tab
    }
  })

  const isOpen = computed(() => !!queryValues.value?.contentType)

  /*
    Open/Close
  */

  const close = () => {
    removeQueryParams()
  }

  const updateQueryParams = (
    contentType: TContentType,
    newKey: string | null,
    mappedRemedyId?: string,
    tab?: TReferenceTab
  ) => {
    const newQueryParams = {
      ...route.query,
      [QueryStrings.contentType]: contentType
    }
    if (newKey) {
      newQueryParams[QueryStrings.key] = newKey
    }
    if (mappedRemedyId) {
      newQueryParams[QueryStrings.mappedRemedy] = mappedRemedyId
    } else {
      delete newQueryParams[QueryStrings.mappedRemedy]
    }
    if (tab) {
      newQueryParams[QueryStrings.tab] = tab
    } else {
      delete newQueryParams[QueryStrings.tab]
    }
    router.push({
      name: route.name ?? undefined,
      params: route.params,
      query: newQueryParams
    })
  }

  const removeQueryParams = () => {
    const query = { ...route.query }
    if (
      query[QueryStrings.contentType] ||
      query[QueryStrings.key] ||
      query[QueryStrings.mappedRemedy] ||
      query[QueryStrings.tab]
    ) {
      delete query[QueryStrings.contentType]
      delete query[QueryStrings.key]
      delete query[QueryStrings.mappedRemedy]
      delete query[QueryStrings.tab]

      router.push({
        name: route.name ?? undefined,
        params: route.params,
        query
      })
    }
  }

  const selectedContentType = computed(() => {
    return queryValues.value?.contentType
  })

  /*
    Open Content Pages
  */
  const openReference = (
    type: TContentType,
    key: string | null,
    origin: TReferenceClickOrigin,
    tab?: TReferenceTab
  ) => {
    // We do not want to track clicks which originate in the guidebook itself,
    // like clicking on a benefit on a remedy.
    if (origin !== 'references') {
      trackButtonClick({
        action: 'viewed_guidebook',
        location: origin,
        value: { type, key }
      })
    }
    updateQueryParams(type, key, undefined, tab)
  }

  const openMappedRemedy = (mappedRemedy: IMappedRemedy) => {
    updateQueryParams('remedy', mappedRemedy.remedy.key, mappedRemedy._id)
  }

  return {
    isOpen,
    close,
    openMappedRemedy,
    openReference,
    selectedContentType,
    queryValues
  }
})
